import React, { useState, useEffect, useRef } from "react";
import { fetchUserProfile, updateUserProfile, changeUserPassword, sendEmailVerification, updateLanguagePreference } from "../services/userService";
import { fetchUserCommunities } from "../services/communityService";
import { uploadProfileImage, fixImageUrl } from "../services/imageService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { useLanguage } from "../contexts/LanguageContext";
import { logoutUser } from "../services/authService";
import "./MobileProfile.css";

// Function to validate email
const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

function MobileProfilePage() {
    const { csrfToken, setAuthState } = useAuth();
    const { isMobile } = useNavigation();
    const { setLanguage, t } = useLanguage();
    const [user, setUser] = useState({
        email: "",
        displayName: "",
        profileImage: "",
        role: "",
        group: "",
        community_id: null,
        email_verified: false,
        preferred_language: "en"
    });

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [passwordData, setPasswordData] = useState({ old_password: "", new_password: "" });
    const [userCommunities, setUserCommunities] = useState([]);
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Active tab state (using same style as MobileEventView)
    const [activeTab, setActiveTab] = useState('profile');
    
    // Fetch user data and communities
    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Fetch user profile
                const userData = await fetchUserProfile();
                if (userData) {
                    setUser(userData);
                    
                    // Fetch user communities
                    try {
                        const communitiesData = await fetchUserCommunities();
                        if (communitiesData && Array.isArray(communitiesData)) {
                            setUserCommunities(communitiesData);
                        }
                    } catch (err) {
                    }
                }
            } catch (error) {
                setError(t('profile.errors.loadingFailed'));
            } finally {
                setLoading(false);
            }
        };

        loadUserData();
    }, [csrfToken]);

    // Handle profile update
    const handleChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    // Handle password input change
    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    // Handle profile update submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        // Validate email
        if (user.email && !validateEmail(user.email)) {
            setError(t('profile.errors.invalidEmail'));
            return;
        }

        try {
            const response = await updateUserProfile(user, csrfToken);
            if (response) {
                setMessage(t('profile.success.profileUpdated'));
            } else {
                setError(t('profile.errors.updateFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || t('errors.generic')));
        }
    };

    // Handle password change submission
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        // Password validation
        if (passwordData.new_password.length < 8) {
            setError(t('profile.errors.passwordTooShort'));
            return;
        }

        try {
            const response = await changeUserPassword(passwordData, csrfToken);
            if (response) {
                setMessage(t('profile.success.passwordChanged'));
                setPasswordData({ old_password: "", new_password: "" });
            } else {
                setError(t('profile.errors.passwordChangeFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || t('errors.generic')));
        }
    };
    
    // Handle sending verification email
    const handleSendVerification = async () => {
        setMessage("");
        setError("");
        
        try {
            const response = await sendEmailVerification(csrfToken);
            if (response) {
                setMessage(t('profile.success.verificationSent'));
                setEmailVerificationSent(true);
            } else {
                setError(t('profile.errors.verificationFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || t('errors.generic')));
        }
    };

    // Trigger click on file input
    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    // Handle profile image file selection
    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
    
        const file = e.target.files[0];
        
        // Simple image type check
        if (!file.type.startsWith('image/')) {
            setError(t('profile.errors.selectImageFile'));
            return;
        }
    
        setMessage("");
        setError("");
        setIsUploading(true);
    
        try {
            // Use uploadProfileImage service function
            const data = await uploadProfileImage(file, csrfToken);
            
            if (data && data.image_url) {
                setUser({ ...user, profileImage: data.image_url });
                setMessage(t('profile.success.imageUploaded'));
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (error) {
            setError(`${t('profile.errors.uploadFailed')}: ${error.message || t('errors.generic')}`);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle language preference change
    const handleLanguageChange = async (e) => {
        const newLanguage = e.target.value;
        setMessage("");
        setError("");

        try {
            const response = await updateLanguagePreference(newLanguage, csrfToken);
            if (response) {
                setUser(prevUser => ({ ...prevUser, preferred_language: newLanguage }));
                // Update the language context
                setLanguage(newLanguage);
                setMessage(t('profile.success.profileUpdated'));
            } else {
                setError(t('profile.errors.updateFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || t('errors.generic')));
        }
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            await logoutUser();
            // Clear auth state
            if (setAuthState) {
                setAuthState({
                    user: null,
                    isAuthenticated: false,
                    csrfToken: null
                });
            }
            // Redirect to login page
            window.location.href = "/login";
        } catch (error) {
            setError(t('errors.generic'));
        }
    };

    if (loading) return (
        <div className="updated-event-view">
            <div className="event-loading">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                </div>
                <p>{t('profile.loading')}</p>
            </div>
        </div>
    );

    // Find primary community
    const primaryCommunity = userCommunities.find(c => c.is_primary);

    // Mobile profile header with logout button
    const renderMobileHeader = () => (
        <div className="mobile-profile-header">
            <div className="profile-image-container">
                {user.profileImage ? (
                    <img 
                        src={fixImageUrl(user.profileImage)} 
                        alt="Profil" 
                        className="profile-image"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            setUser({...user, profileImage: ''});
                        }}
                    />
                ) : (
                    <div className="profile-initials">
                        <span>{user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}</span>
                    </div>
                )}
            </div>
            <h2 className="profile-name">{user.displayName || user.email.split('@')[0]}</h2>
            <div className="profile-details">
                {primaryCommunity && (
                    <span className="profile-community">{primaryCommunity.name}</span>
                )}
            </div>
            <button
                className="logout-button"
                onClick={handleLogout}
            >
                <i className="fas fa-sign-out-alt"></i> {t('navigation.logout')}
            </button>
        </div>
    );

    // Tabs in the style of MobileEventView
    const renderMobileTabs = () => (
        <div className="event-tabs">
            <button
                className={`event-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
            >
                <i className="fas fa-user event-tab-icon"></i>
                {t('profile.title')}
            </button>
            <button
                className={`event-tab ${activeTab === 'communities' ? 'active' : ''}`}
                onClick={() => setActiveTab('communities')}
            >
                <i className="fas fa-users event-tab-icon"></i>
                {t('navigation.communities')}
            </button>
            <button
                className={`event-tab ${activeTab === 'password' ? 'active' : ''}`}
                onClick={() => setActiveTab('password')}
            >
                <i className="fas fa-lock event-tab-icon"></i>
                {t('profile.changePassword')}
            </button>
        </div>
    );

    // Mobile profile content
    const renderMobileContent = () => {
        if (activeTab === 'profile') {
            return (
                <div className="event-content">
                    {(message || error) && (
                        <div className={`alert ${message ? 'alert-success' : 'alert-danger'}`}>
                            {message || error}
                        </div>
                    )}
                    
                    <div className="event-section">
                        <h3 className="event-section-title">{t('profile.profilePhoto')}</h3>
                        <div className="mobile-upload-actions">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                className="d-none"
                                accept="image/*"
                            />
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleUploadClick}
                                disabled={isUploading}
                            >
                                {isUploading ? t('profile.uploading') : t('profile.uploadNewPhoto')}
                            </button>

                            {user.profileImage && (
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setUser({ ...user, profileImage: "" })}
                                >
                                    {t('profile.remove')}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="event-section">
                        <h3 className="event-section-title">{t('profile.accountInformation')}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label">{t('profile.email')}</label>
                                <div className="input-group">
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={user.email || ""}
                                        disabled
                                    />
                                    {!user.email_verified && (
                                        <button
                                            type="button"
                                            className="btn btn-warning"
                                            onClick={handleSendVerification}
                                            disabled={emailVerificationSent}
                                        >
                                            {emailVerificationSent ? t('profile.emailSent') : t('profile.verifyEmail')}
                                        </button>
                                    )}
                                </div>
                                {!user.email_verified ? (
                                    <div className="form-text text-warning">
                                        ⚠️ {t('profile.emailNotVerified')}
                                    </div>
                                ) : (
                                    <div className="form-text text-success">
                                        ✓ {t('profile.emailVerified')}
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t('profile.displayName')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="displayName"
                                    value={user.displayName || ""}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t('profile.profileImageUrl')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="profileImage"
                                    value={user.profileImage || ""}
                                    onChange={handleChange}
                                />
                                <div className="form-text text-muted">
                                    {t('profile.profileImageInstruction')}
                                </div>
                            </div>

                            <button type="submit" className="btn btn-primary w-100">{t('profile.updateProfile')}</button>
                        </form>
                    </div>

                    <div className="event-section">
                        <h3 className="event-section-title">{t('profile.languagePreference')}</h3>
                        <div className="mb-3">
                            <label className="form-label">{t('profile.preferredLanguage')}</label>
                            <select
                                className="form-select"
                                value={user.preferred_language || "en"}
                                onChange={handleLanguageChange}
                            >
                                <option value="en">English</option>
                                <option value="sl">Slovenščina</option>
                                <option value="de">Deutsch</option>
                            </select>
                            <div className="form-text text-muted">
                                {t('profile.languageInstruction')}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (activeTab === 'communities') {
            return (
                <div className="event-content">
                    <div className="event-section">
                        <h3 className="event-section-title">{t('profile.communityInformation')}</h3>

                        <div className="mb-3">
                            <label className="form-label">{t('profile.primaryCommunity')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={primaryCommunity ? primaryCommunity.name : t('profile.none')}
                                disabled
                            />
                        </div>

                        {userCommunities.length > 0 ? (
                            <div className="mb-3">
                                <label className="form-label">{t('profile.myCommunities')}</label>
                                <ul className="list-group event-attendees-list">
                                    {userCommunities.map(community => (
                                        <li key={community.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            {community.name}
                                            <div>
                                                {community.is_primary && (
                                                    <span className="badge bg-primary rounded-pill me-1">{t('profile.primary')}</span>
                                                )}
                                                <span className="badge bg-secondary rounded-pill">{community.role}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                {t('communities.noAvailableCommunities')}
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">{t('profile.globalRole')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={user.role || t('profile.user')}
                                disabled
                            />
                        </div>

                        {user.group && (
                            <div className="mb-3">
                                <label className="form-label">{t('profile.group')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={user.group}
                                    disabled
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        } else if (activeTab === 'password') {
            return (
                <div className="event-content">
                    {(message || error) && (
                        <div className={`alert ${message ? 'alert-success' : 'alert-danger'}`}>
                            {message || error}
                        </div>
                    )}

                    <div className="event-section">
                        <h3 className="event-section-title">{t('profile.changePassword')}</h3>
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="mb-3">
                                <label className="form-label">{t('profile.currentPassword')}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    name="old_password"
                                    value={passwordData.old_password}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{t('profile.newPassword')}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    name="new_password"
                                    value={passwordData.new_password}
                                    onChange={handlePasswordChange}
                                    required
                                />
                                <div className="form-text text-muted">
                                    {t('profile.passwordMinLength')}
                                </div>
                            </div>
                            <button type="submit" className="btn btn-danger w-100">{t('profile.changePasswordButton')}</button>
                        </form>
                    </div>
                </div>
            );
        }
    };

    // Desktop view is not needed, but could be implemented if required
    if (!isMobile) {
        // Return the original desktop layout from ProfilePage.js
        return null; // Would implement if needed
    }

    // Mobile view with styling similar to MobileEventView
    return (
        <div className="updated-event-view">
            <div className="event-scrollable-content">
                {renderMobileHeader()}
                {renderMobileTabs()}
                {renderMobileContent()}
            </div>
        </div>
    );
}

export default MobileProfilePage;